import { useEffect, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";
import FloorPlanViewer from "./FloorPlanViewer";

const KIND_META = {
  floorplan: { label: "Floor plan", icon: "Ruler", color: "#8b5cf6" },
  brochure: { label: "Brochure", icon: "BookOpen", color: "#3b82f6" },
  epc: { label: "Energy (EPC)", icon: "Zap", color: "#10b981" },
  deed: { label: "Deed / Title", icon: "FileSignature", color: "#f59e0b" },
  survey: { label: "Survey", icon: "ClipboardCheck", color: "#ef4444" },
  other: { label: "Document", icon: "FileText", color: "#a1a1aa" },
};

/**
 * Public document tray shown beneath the share-page reel.  Auto-hides if
 * no docs exist.  PDFs and images open in an inline modal viewer (no
 * download required); anything else falls back to a new-tab open.
 */
export default function DocumentsAccordion({ listingId, brand }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [viewing, setViewing] = useState(null); // doc currently in modal viewer

  useEffect(() => {
    if (!listingId) {
      setLoading(false);
      return;
    }
    axios
      .get(`${PUBLIC_BASE}/api/share/listings/${listingId}/documents`)
      .then((r) => setDocs(r.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [listingId]);

  if (loading || docs.length === 0) return null;

  const isPreviewable = (d) => {
    const ct = d.content_type || "";
    return ct.startsWith("application/pdf") || ct.startsWith("image/");
  };

  return (
    <div
      className="mt-6 rounded-2xl border bg-[#0A0A0A]"
      style={{ borderColor: brand ? `${brand}30` : "rgb(255 255 255 / 0.1)" }}
      data-testid="documents-accordion"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
        data-testid="documents-accordion-toggle"
      >
        <Icons.FolderOpen size={16} style={{ color: brand || "#a1a1aa" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: brand || "#71717a" }}>
            Property documents
          </div>
          <div className="font-display text-base text-white">
            {docs.length} file{docs.length === 1 ? "" : "s"} — tap to view
          </div>
        </div>
        <Icons.ChevronDown
          size={16}
          className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-2">
          {docs.map((d) => {
            const meta = KIND_META[d.kind] || KIND_META.other;
            const I = Icons[meta.icon] || Icons.FileText;
            const previewable = isPreviewable(d);
            const onActivate = (e) => {
              if (previewable) {
                e.preventDefault();
                setViewing(d);
              }
              // else <a> falls through to new-tab open
            };
            return (
              <a
                key={d.id}
                href={`${PUBLIC_BASE}/api/public/documents/${d.id}`}
                target="_blank"
                rel="noreferrer"
                onClick={onActivate}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/30 transition-all group"
                data-testid={`document-${d.id}`}
              >
                <div
                  className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: `${meta.color}20`, color: meta.color }}
                >
                  <I size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{d.label || d.filename}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">
                    {meta.label}
                    {d.size > 0 && (
                      <span className="text-zinc-600 normal-case tracking-normal ml-2">
                        · {fmtBytes(d.size)}
                      </span>
                    )}
                  </div>
                </div>
                {previewable ? (
                  <Icons.Eye
                    size={14}
                    className="text-zinc-500 group-hover:text-white shrink-0 transition-colors"
                  />
                ) : (
                  <Icons.ExternalLink
                    size={14}
                    className="text-zinc-500 group-hover:text-white shrink-0 transition-colors"
                  />
                )}
              </a>
            );
          })}
        </div>
      )}
      {viewing && (
        <FloorPlanViewer
          doc={viewing}
          brand={brand}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
